import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaCheck, FaEllipsisV, FaPalette, FaPen, FaPlus, FaTag, FaTimes, FaTrash } from "react-icons/fa";

// Reusable local tag picker. Visually mirrors the caas-console TagPicker
// (colored pills + tag-icon trigger + searchable dropdown), while callers own
// the tag catalogue and persistence strategy.
const TAG_COLOR_PALETTE = [
  "#1F7A8C", "#26734D", "#2B50AA", "#6A4C93", "#B23A48",
  "#C36F09", "#3D5A80", "#2A9D8F", "#6D6875", "#3A5A40",
  "#7B2CBF", "#197278", "#4361EE", "#9A031E", "#457B9D",
];

export const slugify = (label) => String(label || "").trim().toLowerCase().replace(/\s+/g, "-");
export const toTag = (label) => {
  const details = String(label || "").trim();
  return details ? { tag_id: slugify(details), details } : null;
};

export const normalizeTags = (value) => {
  const source = Array.isArray(value) ? value : String(value || "").split(",");
  const seen = new Set();
  return source
    .map((item) => (typeof item === "object" ? item : toTag(item)))
    .filter((tag) => {
      if (!tag?.tag_id || seen.has(String(tag.tag_id))) return false;
      seen.add(String(tag.tag_id));
      return true;
    })
    .map((tag) => ({ ...tag, details: tag.details || tag.label || tag.name || tag.tag_id }));
};

export const serializeTags = (value) => normalizeTags(value).map((tag) => tag.tag_id).join(",");

export const getTagIds = (value) => normalizeTags(value).map((tag) => tag.tag_id);

export const colorForTag = (tagOrId) => {
  if (typeof tagOrId === "object" && tagOrId?.color) return tagOrId.color;
  const key = String(typeof tagOrId === "object" ? tagOrId?.tag_id : tagOrId || "");
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return TAG_COLOR_PALETTE[hash % TAG_COLOR_PALETTE.length];
};

const sameTag = (a, b) => String(a?.tag_id) === String(b?.tag_id);

const TagPicker = ({
  value = [],
  onChange,
  suggestions = [],
  disabled = false,
  saving = false,
  label = "Tags",
  placeholder = "Search or create tags",
  allowCreate = true,
  compact = false,
  variant = "broker",
  testId,
  // Optional registry management — when provided, each suggestion gets a
  // rename / change-colour / delete action menu (caas-console style).
  onRenameTag,
  onRecolorTag,
  onDeleteTag,
}) => {
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuStyle, setMenuStyle] = useState(null);
  const [actionMenuTagId, setActionMenuTagId] = useState(null);
  const [colorMenuTagId, setColorMenuTagId] = useState(null);
  const [editingTagId, setEditingTagId] = useState(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [busyTagId, setBusyTagId] = useState(null);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const actionMenuRef = useRef(null);
  const actionMenuAnchorRef = useRef(null);
  const colorMenuRef = useRef(null);
  const colorMenuAnchorRef = useRef(null);
  const [actionMenuStyle, setActionMenuStyle] = useState({});
  const [colorMenuStyle, setColorMenuStyle] = useState({});

  const canManage = Boolean(onRenameTag || onRecolorTag || onDeleteTag);
  const closeTagMenus = () => {
    setActionMenuTagId(null);
    setColorMenuTagId(null);
    actionMenuAnchorRef.current = null;
    colorMenuAnchorRef.current = null;
    setActionMenuStyle({});
    setColorMenuStyle({});
  };
  const cancelEdit = () => { setEditingTagId(null); setEditingLabel(""); };

  const normalizedSuggestions = useMemo(() => normalizeTags(suggestions), [suggestions]);

  const suggestionLookup = useMemo(() => {
    const byId = new Map();
    const byLabel = new Map();
    normalizedSuggestions.forEach((tag) => {
      byId.set(String(tag.tag_id), tag);
      byLabel.set(String(tag.details || "").trim().toLowerCase(), tag);
    });
    return { byId, byLabel };
  }, [normalizedSuggestions]);

  const selected = useMemo(
    () => normalizeTags(value).map((tag) => (
      suggestionLookup.byId.get(String(tag.tag_id))
      || suggestionLookup.byLabel.get(String(tag.details || "").trim().toLowerCase())
      || tag
    )),
    [value, suggestionLookup],
  );

  const tagDirectory = useMemo(() => {
    const byId = new Map();
    normalizedSuggestions.forEach((tag) => {
      if (tag?.tag_id) byId.set(String(tag.tag_id), tag);
    });
    selected.forEach((tag) => {
      if (tag?.tag_id && !byId.has(String(tag.tag_id))) byId.set(String(tag.tag_id), tag);
    });
    return byId;
  }, [normalizedSuggestions, selected]);

  const actionMenuTag = useMemo(
    () => tagDirectory.get(String(actionMenuTagId)) || null,
    [actionMenuTagId, tagDirectory],
  );

  const colorMenuTag = useMemo(
    () => tagDirectory.get(String(colorMenuTagId)) || null,
    [colorMenuTagId, tagDirectory],
  );

  const options = useMemo(() => {
    const needle = draft.trim().toLowerCase();
    const seen = new Set(selected.map((tag) => String(tag.tag_id)));
    return normalizedSuggestions
      .filter((tag) => tag && tag.tag_id && !seen.has(String(tag.tag_id)))
      .filter((tag) => !needle || String(tag.details || tag.tag_id).toLowerCase().includes(needle))
      .sort((a, b) => String(a.details || "").localeCompare(String(b.details || "")));
  }, [normalizedSuggestions, selected, draft]);

  const trimmed = draft.trim();
  const exists = useMemo(() => {
    const id = slugify(trimmed);
    const label = trimmed.toLowerCase();
    return selected.some((tag) => String(tag.tag_id) === id || String(tag.details || "").trim().toLowerCase() === label)
      || options.some((tag) => String(tag.tag_id) === id || String(tag.details || "").trim().toLowerCase() === label);
  }, [selected, options, trimmed]);
  const canCreate = allowCreate && trimmed.length > 0 && !exists;

  useEffect(() => {
    if (!open && !actionMenuTagId && !colorMenuTagId && !editingTagId) return undefined;
    const onDocClick = (event) => {
      if (
        wrapRef.current?.contains(event.target)
        || menuRef.current?.contains(event.target)
        || actionMenuRef.current?.contains(event.target)
        || colorMenuRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
      closeTagMenus();
      cancelEdit();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, actionMenuTagId, colorMenuTagId, editingTagId]);

  useEffect(() => { setActiveIndex(-1); closeTagMenus(); cancelEdit(); }, [draft, open]);
  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  useEffect(() => {
    if (!actionMenuTagId) return undefined;

    const positionActionMenu = () => {
      const anchor = actionMenuAnchorRef.current;
      if (!anchor) return;
      const anchorRect = anchor.getBoundingClientRect();
      const spacing = 8;
      const viewport = window.visualViewport;
      const viewportLeft = viewport?.offsetLeft || 0;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportWidth = viewport?.width || document.documentElement.clientWidth || window.innerWidth;
      const viewportHeight = viewport?.height || document.documentElement.clientHeight || window.innerHeight;
      const viewportRight = viewportLeft + viewportWidth;
      const viewportBottom = viewportTop + viewportHeight;
      const menuRect = actionMenuRef.current?.getBoundingClientRect();
      const menuWidth = Math.max(menuRect?.width || 170, 170);
      const menuHeight = Math.max(menuRect?.height || 130, 130);
      let left = anchorRect.right - menuWidth;
      if (left + menuWidth > viewportRight - spacing) left = viewportRight - menuWidth - spacing;
      if (left < viewportLeft + spacing) left = Math.max(viewportLeft + spacing, anchorRect.left);
      let top = anchorRect.bottom + spacing;
      if (top + menuHeight > viewportBottom - spacing) top = anchorRect.top - menuHeight - spacing;
      if (top < viewportTop + spacing) top = viewportTop + spacing;
      setActionMenuStyle({
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
        minWidth: `${menuWidth}px`,
        zIndex: 1000010,
      });
    };

    positionActionMenu();
    const frame = requestAnimationFrame(positionActionMenu);
    window.addEventListener("scroll", positionActionMenu, true);
    window.addEventListener("resize", positionActionMenu);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", positionActionMenu, true);
      window.removeEventListener("resize", positionActionMenu);
    };
  }, [actionMenuTagId]);

  useEffect(() => {
    if (!colorMenuTagId) return undefined;

    const positionColorMenu = () => {
      const anchor = colorMenuAnchorRef.current || actionMenuAnchorRef.current || actionMenuRef.current;
      if (!anchor) return;
      const anchorRect = anchor.getBoundingClientRect();
      const spacing = 8;
      const viewport = window.visualViewport;
      const viewportLeft = viewport?.offsetLeft || 0;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportWidth = viewport?.width || document.documentElement.clientWidth || window.innerWidth;
      const viewportHeight = viewport?.height || document.documentElement.clientHeight || window.innerHeight;
      const viewportRight = viewportLeft + viewportWidth;
      const viewportBottom = viewportTop + viewportHeight;
      const menuRect = colorMenuRef.current?.getBoundingClientRect();
      const menuWidth = Math.max(menuRect?.width || 190, 190);
      const menuHeight = Math.max(menuRect?.height || 120, 120);
      let left = anchorRect.right + spacing;
      if (left + menuWidth > viewportRight - spacing) left = anchorRect.left - menuWidth - spacing;
      if (left < viewportLeft + spacing) left = viewportLeft + spacing;
      let top = anchorRect.top;
      if (top + menuHeight > viewportBottom - spacing) top = viewportBottom - menuHeight - spacing;
      if (top < viewportTop + spacing) top = viewportTop + spacing;
      setColorMenuStyle({
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
        minWidth: `${menuWidth}px`,
        zIndex: 1000020,
      });
    };

    positionColorMenu();
    const frame = requestAnimationFrame(positionColorMenu);
    window.addEventListener("scroll", positionColorMenu, true);
    window.addEventListener("resize", positionColorMenu);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", positionColorMenu, true);
      window.removeEventListener("resize", positionColorMenu);
    };
  }, [colorMenuTagId]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }

    const updateMenuStyle = () => {
      const rect = triggerRef.current?.getBoundingClientRect() || wrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      const margin = 8;
      const viewport = window.visualViewport;
      const viewportLeft = viewport?.offsetLeft || 0;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportWidth = viewport?.width || document.documentElement.clientWidth || window.innerWidth;
      const viewportHeight = viewport?.height || document.documentElement.clientHeight || window.innerHeight;
      const viewportRight = viewportLeft + viewportWidth;
      const viewportBottom = viewportTop + viewportHeight;
      const width = Math.max(280, Math.min(compact ? Math.max(rect.width, 280) : 320, viewportWidth - (margin * 2)));
      let left = rect.left;
      if (left + width > viewportRight - margin) left = rect.right - width;
      if (left + width > viewportRight - margin) left = viewportRight - width - margin;
      if (left < viewportLeft + margin) left = viewportLeft + margin;
      const menuHeight = Math.max(menuRef.current?.getBoundingClientRect?.().height || 330, 260);
      const openUp = viewportBottom - rect.bottom < menuHeight + 12 && rect.top - viewportTop > menuHeight + 12;
      const top = Math.max(viewportTop + margin, rect.bottom + 6);
      const bottom = Math.max(margin, viewportBottom - rect.top + 6);
      setMenuStyle({
        position: "fixed",
        left: `${left}px`,
        width: `${width}px`,
        // Portaled to <body>; must sit above the application detail drawer/modal
        // (the broker drawer uses .___modal at z-index 999999).
        zIndex: 1000000,
        ...(openUp ? { bottom: `${bottom}px`, top: "auto" } : { top: `${top}px`, bottom: "auto" }),
      });
    };

    updateMenuStyle();
    const frame = requestAnimationFrame(updateMenuStyle);
    window.addEventListener("scroll", updateMenuStyle, true);
    window.addEventListener("resize", updateMenuStyle);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", updateMenuStyle, true);
      window.removeEventListener("resize", updateMenuStyle);
    };
  }, [compact, open]);

  const addTag = (tag) => {
    if (!tag || disabled) return;
    if (selected.some((existing) => sameTag(existing, tag))) { setDraft(""); return; }
    onChange?.([...selected, tag]);
    setDraft("");
    setActiveIndex(-1);
  };

  const removeTag = (tagId) => {
    if (disabled) return;
    onChange?.(selected.filter((tag) => String(tag.tag_id) !== String(tagId)));
  };

  const toggleActionMenu = (tagId, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (disabled) return;
    setColorMenuTagId(null);
    colorMenuAnchorRef.current = null;
    if (actionMenuTagId === tagId) {
      closeTagMenus();
      return;
    }
    actionMenuAnchorRef.current = event?.currentTarget || null;
    setActionMenuTagId(tagId);
  };

  const startRename = (tag) => {
    setEditingTagId(tag.tag_id);
    setEditingLabel(tag.details || "");
    closeTagMenus();
  };

  const runManage = async (tagId, action) => {
    setBusyTagId(tagId);
    try {
      await action();
    } finally {
      setBusyTagId(null);
    }
  };

  const commitRename = (tag) => {
    const next = editingLabel.trim();
    cancelEdit();
    if (!onRenameTag || !next || next === tag.details) return;
    runManage(tag.tag_id, () => onRenameTag(tag, next));
  };

  const handleRecolor = (tag, color) => {
    closeTagMenus();
    if (!onRecolorTag) return;
    runManage(tag.tag_id, () => onRecolorTag(tag, color));
  };

  const openColorMenu = (tag, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!onRecolorTag || disabled) return;
    colorMenuAnchorRef.current = event?.currentTarget || actionMenuRef.current;
    setColorMenuTagId(tag.tag_id);
  };

  const handleDelete = (tag) => {
    closeTagMenus();
    if (!onDeleteTag) return;
    runManage(tag.tag_id, async () => {
      await onDeleteTag(tag);
      if (selected.some((existing) => sameTag(existing, tag))) {
        onChange?.(selected.filter((existing) => !sameTag(existing, tag)));
      }
    });
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((prev) => Math.min(prev + 1, options.length - 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
      return;
    }
    if (event.key === "Escape") { setOpen(false); return; }
    if (event.key === "Backspace" && !draft && selected.length) {
      removeTag(selected[selected.length - 1].tag_id);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (activeIndex >= 0 && options[activeIndex]) { addTag(options[activeIndex]); return; }
      if (canCreate) addTag(toTag(trimmed));
    }
  };

  const pickerMenu = open ? (
    <div
      className={`rw-tag-picker rw-tag-picker--${variant} ${compact ? "rw-tag-picker--compact" : ""} rw-tag-picker__portal-root`.trim()}
    >
      <div className="rw-tag-picker__menu" role="listbox" style={menuStyle || undefined} ref={menuRef}>
        <div className="rw-tag-picker__menu-header">
          <div className="rw-tag-picker__menu-title">{label}</div>
          <div className="rw-tag-picker__menu-subtitle">{selected.length} selected</div>
        </div>
        <div className="rw-tag-picker__search">
          <input
            ref={inputRef}
            type="text"
            className="rw-tag-picker__input"
            value={draft}
            placeholder={placeholder}
            onChange={(event) => { setDraft(event.target.value); setOpen(true); }}
            onKeyDown={handleKeyDown}
            maxLength={30}
          />
          {draft ? (
            <button type="button" className="rw-tag-picker__clear" onClick={() => setDraft("")} aria-label="Clear search">
              <FaTimes />
            </button>
          ) : null}
        </div>
        <div className="rw-tag-picker__list">
          {selected.map((tag) => {
            const color = colorForTag(tag);
            const isEditing = editingTagId === tag.tag_id;
            const busy = busyTagId === tag.tag_id;
            return (
              <div
                key={`selected-${tag.tag_id}`}
                className={`rw-tag-picker__option rw-tag-picker__option--selected${busy ? " is-busy" : ""}`}
                style={{ backgroundColor: `${color}22` }}
                role="option"
                aria-selected="true"
              >
                {isEditing ? (
                  <input
                    className="rw-tag-picker__edit-input"
                    value={editingLabel}
                    autoFocus
                    maxLength={30}
                    onMouseDown={(event) => event.stopPropagation()}
                    onChange={(event) => setEditingLabel(event.target.value)}
                    onBlur={() => commitRename(tag)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") { event.preventDefault(); commitRename(tag); }
                      if (event.key === "Escape") { event.preventDefault(); cancelEdit(); }
                    }}
                  />
                ) : (
                  <>
                    <span className="rw-tag-picker__option-label" style={{ color }}>
                      <FaCheck className="rw-tag-picker__option-selected-icon" aria-hidden="true" />
                      {tag.details || tag.tag_id}
                    </span>
                    {canManage ? (
                      <button
                        type="button"
                        className="rw-tag-picker__option-action"
                        aria-label={`Tag actions for ${tag.details || tag.tag_id}`}
                        onMouseDown={(event) => toggleActionMenu(tag.tag_id, event)}
                      >
                        <FaEllipsisV />
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            );
          })}
          {selected.length && options.length ? (
            <div className="rw-tag-picker__divider">Available tags</div>
          ) : null}
          {options.map((tag, index) => {
            const color = colorForTag(tag);
            const isEditing = editingTagId === tag.tag_id;
            const busy = busyTagId === tag.tag_id;
            return (
              <div
                key={tag.tag_id}
                className={`rw-tag-picker__option${index === activeIndex ? " is-active" : ""}${busy ? " is-busy" : ""}`}
                style={{ backgroundColor: `${color}22` }}
                role="option"
                aria-selected={index === activeIndex}
                onMouseEnter={() => setActiveIndex(index)}
              >
                {isEditing ? (
                  <input
                    className="rw-tag-picker__edit-input"
                    value={editingLabel}
                    autoFocus
                    maxLength={30}
                    onMouseDown={(event) => event.stopPropagation()}
                    onChange={(event) => setEditingLabel(event.target.value)}
                    onBlur={() => commitRename(tag)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") { event.preventDefault(); commitRename(tag); }
                      if (event.key === "Escape") { event.preventDefault(); cancelEdit(); }
                    }}
                  />
                ) : (
                  <>
                    <span
                      className="rw-tag-picker__option-label"
                      style={{ color }}
                      onMouseDown={(event) => { event.preventDefault(); addTag(tag); }}
                    >
                      {tag.details || tag.tag_id}
                    </span>
                    {canManage ? (
                      <button
                        type="button"
                        className="rw-tag-picker__option-action"
                        aria-label={`Tag actions for ${tag.details || tag.tag_id}`}
                        onMouseDown={(event) => toggleActionMenu(tag.tag_id, event)}
                      >
                        <FaEllipsisV />
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            );
          })}
          {!selected.length && !options.length && !canCreate ? (
            <div className="rw-tag-picker__empty">No tags found.</div>
          ) : null}
        </div>
        {canCreate ? (
          <button
            type="button"
            className="rw-tag-picker__hint"
            onMouseDown={(event) => { event.preventDefault(); addTag(toTag(trimmed)); }}
          >
            <FaPlus aria-hidden="true" /> Create &ldquo;{trimmed}&rdquo;
          </button>
        ) : null}
      </div>
    </div>
  ) : null;

  return (
    <div
      className={`rw-tag-picker rw-tag-picker--${variant} ${compact ? "rw-tag-picker--compact" : ""}${disabled ? " rw-tag-picker--disabled" : ""}`.trim()}
      ref={wrapRef}
      aria-busy={saving}
      data-testid={testId}
    >
      <div className="rw-tag-picker__pill-list">
        {selected.map((tag) => {
          const color = colorForTag(tag);
          // While the dropdown is open the same tag is also rendered as a
          // selected option there; let that input own the rename so we never
          // mount two autoFocus inputs for one tag (they'd steal focus from
          // each other and the blur would immediately cancel the edit).
          const isEditing = editingTagId === tag.tag_id && !open;
          return (
            <span
              className="rw-tag-picker__pill rw-tag-picker__pill--removable"
              key={tag.tag_id}
              style={{ color, backgroundColor: `${color}22` }}
            >
              {isEditing ? (
                <input
                  className="rw-tag-picker__pill-edit-input"
                  value={editingLabel}
                  autoFocus
                  maxLength={30}
                  onMouseDown={(event) => event.stopPropagation()}
                  onChange={(event) => setEditingLabel(event.target.value)}
                  onBlur={() => commitRename(tag)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") { event.preventDefault(); commitRename(tag); }
                    if (event.key === "Escape") { event.preventDefault(); cancelEdit(); }
                  }}
                />
              ) : (
                <>
                  <span className="rw-tag-picker__pill-label">{tag.details || tag.tag_id}</span>
                  {/* Rename / recolour / delete live in the dropdown's action
                      menu; the pill chip only needs its remove (×) button. */}
                  {!disabled ? (
                    <button
                      type="button"
                      className="rw-tag-picker__pill-remove"
                      onClick={() => removeTag(tag.tag_id)}
                      aria-label={`Remove tag ${tag.details || tag.tag_id}`}
                    >
                      <FaTimes />
                    </button>
                  ) : null}
                </>
              )}
            </span>
          );
        })}
      </div>

      <div className="rw-tag-picker__trigger-wrapper" ref={triggerRef}>
        <button
          type="button"
          className="rw-tag-picker__trigger"
          onClick={(event) => {
            if (disabled) return;
            event.stopPropagation();
            setOpen((prev) => !prev);
          }}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={label}
          disabled={disabled}
        >
          <FaTag />
        </button>
        {saving ? (
          <span className="rw-tag-picker__inline-status" aria-live="polite">
            <span className="rw-tag-picker__spinner" />
          </span>
        ) : null}

        {pickerMenu && typeof document !== "undefined" ? createPortal(pickerMenu, document.body) : pickerMenu}

        {canManage && actionMenuTag && editingTagId !== actionMenuTag.tag_id && typeof document !== "undefined" ? createPortal((
          <div
            className="rw-tag-picker__action-menu rw-tag-picker__action-menu--broker"
            role="menu"
            ref={actionMenuRef}
            style={actionMenuStyle}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {onRenameTag ? (
              <button
                type="button"
                role="menuitem"
                onClick={(event) => {
                  event.preventDefault();
                  startRename(actionMenuTag);
                }}
              >
                <FaPen aria-hidden="true" /> Rename
              </button>
            ) : null}
            {onRecolorTag ? (
              <button
                type="button"
                role="menuitem"
                onClick={(event) => openColorMenu(actionMenuTag, event)}
              >
                <FaPalette aria-hidden="true" /> Change colour
              </button>
            ) : null}
            {onDeleteTag ? (
              <button
                type="button"
                role="menuitem"
                className="is-danger"
                onClick={(event) => {
                  event.preventDefault();
                  handleDelete(actionMenuTag);
                }}
              >
                <FaTrash aria-hidden="true" /> Delete
              </button>
            ) : null}
          </div>
        ), document.body) : null}

        {canManage && colorMenuTag && typeof document !== "undefined" ? createPortal((
          <div
            className="rw-tag-picker__color-menu rw-tag-picker__color-menu--broker"
            ref={colorMenuRef}
            style={colorMenuStyle}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {TAG_COLOR_PALETTE.map((swatch) => (
              <button
                key={swatch}
                type="button"
                className="rw-tag-picker__swatch"
                style={{ backgroundColor: swatch }}
                aria-label={`Set colour ${swatch}`}
                onClick={(event) => {
                  event.preventDefault();
                  handleRecolor(colorMenuTag, swatch);
                }}
              >
                {String(colorForTag(colorMenuTag)).toLowerCase() === swatch.toLowerCase() ? <FaCheck aria-hidden="true" /> : null}
              </button>
            ))}
          </div>
        ), document.body) : null}
      </div>
    </div>
  );
};

export default TagPicker;
